/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Shield, Search, Save, RotateCcw, Download, Lock, Sparkles, Upload, Users, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  agent: "Agent",
  manager: "Manager",
  backoffice: "Backoffice",
  admin: "Admin",
};

const PERM_LABELS = {
  hidden: "Ascuns",
  read: "Citește",
  write: "Editează",
};

const PERM_BADGE = {
  hidden: "bg-slate-100 text-slate-600 border-slate-200",
  read: "bg-sky-100 text-sky-900 border-sky-200",
  write: "bg-emerald-100 text-emerald-900 border-emerald-200",
};

const PERM_SELECT_CLASS = {
  hidden: "border-slate-300 text-slate-700",
  read: "border-sky-300 text-sky-900",
  write: "border-emerald-300 text-emerald-900",
};

export default function AdminFieldAccess() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [catalog, setCatalog] = useState(null);
  const [rules, setRules] = useState([]); // [{field_key, role, permission, source}]
  const [orphans, setOrphans] = useState([]);
  const [pending, setPending] = useState({}); // { "fk|role": "permission" }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Iter38b — Profiles + CSV import + blast radius
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [profilePreview, setProfilePreview] = useState(null); // {profile, change_count, diff, affected_roles, affected_users_by_role}
  const [profileApplyOpen, setProfileApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null); // {filename, change_count, diff, file}
  const [csvDiffOpen, setCsvDiffOpen] = useState(false);
  const [blastRadius, setBlastRadius] = useState({}); // {agent: N, manager: M, ...}


  // filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showSensitiveOnly, setShowSensitiveOnly] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cat, rls] = await Promise.all([
        axios.get(`${API}/api/admin/field-access/catalog`),
        axios.get(`${API}/api/admin/field-access/rules`),
      ]);
      setCatalog(cat.data);
      setRules(rls.data.rules || []);
      setOrphans(rls.data.orphans || []);
      setPending({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea matricei");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Iter38b — load profiles list + blast radius counts in parallel with main data
  useEffect(() => {
    (async () => {
      try {
        const [p, br] = await Promise.all([
          axios.get(`${API}/api/admin/field-access/profiles`),
          axios.get(`${API}/api/admin/users/active-by-role`),
        ]);
        setProfiles(p.data.profiles || []);
        setBlastRadius(br.data.by_role || {});
      } catch (e) {
        // non-fatal — main matrix still works
      }
    })();
  }, []);


  // Build lookup: { fieldKey: { role: { permission, source } } }
  const rulesMatrix = useMemo(() => {
    const m = {};
    for (const r of rules) {
      if (!m[r.field_key]) m[r.field_key] = {};
      m[r.field_key][r.role] = { permission: r.permission, source: r.source };
    }
    return m;
  }, [rules]);

  const categories = useMemo(() => {
    if (!catalog) return [];
    const seen = [];
    for (const f of catalog.fields) {
      if (!seen.includes(f.category)) seen.push(f.category);
    }
    return seen;
  }, [catalog]);

  const filteredFields = useMemo(() => {
    if (!catalog) return [];
    let arr = catalog.fields;
    if (categoryFilter !== "all") arr = arr.filter((f) => f.category === categoryFilter);
    if (showSensitiveOnly) arr = arr.filter((f) => f.sensitive);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (f) =>
          f.label.toLowerCase().includes(q) ||
          f.field_key.toLowerCase().includes(q) ||
          (f.tech_name || "").toLowerCase().includes(q) ||
          (f.description || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [catalog, search, categoryFilter, showSensitiveOnly]);

  const pendingCount = Object.keys(pending).length;

  const getEffectivePerm = (field_key, role) => {
    const k = `${field_key}|${role}`;
    if (pending[k] !== undefined) return pending[k];
    return rulesMatrix[field_key]?.[role]?.permission || "hidden";
  };

  const handleChange = (field_key, role, newPerm) => {
    const current = rulesMatrix[field_key]?.[role]?.permission || "hidden";
    const k = `${field_key}|${role}`;
    setPending((prev) => {
      const next = { ...prev };
      if (newPerm === current) delete next[k];
      else next[k] = newPerm;
      return next;
    });
  };

  const handleReset = () => {
    if (!pendingCount) return;
    if (!window.confirm(`Renunți la ${pendingCount} modificări nesalvate?`)) return;
    setPending({});
    toast.info("Modificările au fost anulate.");
  };

  const handleSave = async () => {
    if (!pendingCount) return;
    setSaving(true);
    try {
      const payload = {
        rules: Object.entries(pending).map(([k, permission]) => {
          const [field_key, role] = k.split("|");
          return { field_key, role, permission };
        }),
      };
      const r = await axios.put(`${API}/api/admin/field-access/rules`, payload);
      toast.success(
        `Matrice actualizată: ${r.data.modified} modificări, ${r.data.deleted} reset la default, ${r.data.noop} noop.`
      );
      setConfirmOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    if (!catalog) return;
    const header = [
      "Categorie", "Câmp (label)", "field_key", "Nume tehnic DB", "Sensibil",
      ...catalog.roles.map((r) => ROLE_LABELS[r] || r),
      "Descriere",
    ];
    const rows = catalog.fields.map((f) => {
      const perms = catalog.roles.map((role) => {
        const eff = getEffectivePerm(f.field_key, role);
        const src = rulesMatrix[f.field_key]?.[role]?.source || "default";
        return `${PERM_LABELS[eff] || eff}${src === "db" ? " (override)" : ""}`;
      });
      return [
        f.category, f.label, f.field_key, f.tech_name, f.sensitive ? "DA" : "",
        ...perms,
        f.description,
      ];
    });
    const csv = [header, ...rows]
      .map((row) =>
        row.map((c) => {
          const v = c == null ? "" : String(c);
          if (v.includes(",") || v.includes('"') || v.includes("\n")) {
            return `"${v.replace(/"/g, '""')}"`;
          }
          return v;
        }).join(",")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `field_access_matrix_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ───── Iter38b — Profile apply ─────
  const handlePreviewProfile = async (profileKey) => {
    if (!profileKey) {
      setProfilePreview(null);
      return;
    }
    try {
      const r = await axios.post(`${API}/api/admin/field-access/profiles/${profileKey}/preview`);
      setProfilePreview(r.data);
      setProfileApplyOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la preview profil");
    }
  };

  const handleApplyProfile = async () => {
    if (!selectedProfile) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/api/admin/field-access/profiles/${selectedProfile}/apply`);
      toast.success(
        `Profil aplicat: ${r.data.changed} modificări (${r.data.modified} update, ${r.data.deleted} reset).`
      );
      setProfileApplyOpen(false);
      setProfilePreview(null);
      setSelectedProfile("");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la aplicare profil");
    } finally {
      setApplying(false);
    }
  };

  // ───── Iter38b — CSV import ─────
  const handleCsvFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    setCsvImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/admin/field-access/import-csv?dry_run=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCsvPreview({ ...r.data, file });
      setCsvDiffOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la parsarea CSV");
    } finally {
      setCsvImporting(false);
    }
  };

  const handleApplyCsv = async () => {
    if (!csvPreview?.file) return;
    setCsvImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", csvPreview.file);
      const r = await axios.post(
        `${API}/api/admin/field-access/import-csv?dry_run=false`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `CSV aplicat: ${r.data.changed} modificări (${r.data.modified} update, ${r.data.deleted} reset).`
      );
      setCsvDiffOpen(false);
      setCsvPreview(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la aplicare CSV");
    } finally {
      setCsvImporting(false);
    }
  };

  // ───── Iter38b — Blast radius helper for save modal ─────
  const pendingBlastRadius = useMemo(() => {
    const roles = new Set();
    Object.keys(pending).forEach((k) => {
      const [, role] = k.split("|");
      roles.add(role);
    });
    return Array.from(roles).map((r) => ({
      role: r,
      label: ROLE_LABELS[r] || r,
      count: blastRadius[r] || 0,
    }));
  }, [pending, blastRadius]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!catalog) {
    return <div className="p-6 text-rose-700">Nu s-a putut încărca catalogul.</div>;
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-field-access-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-slate-700" />
            <h1 className="text-2xl font-semibold tracking-tight">Câmpuri & Acces</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Matricea de acces pe câmpuri × roluri. Aici poți vedea fiecare câmp expus în CRM și
            configura nivelele de acces pentru fiecare tip de utilizator. Modificările sunt aplicate
            imediat în UI și endpoint-urile API (FLAC enforcement activ), salvate în DB și auditate.
          </p>
          {!isAdmin && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900" data-testid="readonly-banner">
              <Lock className="h-3.5 w-3.5" />
              Vizualizare read-only — doar admin poate modifica matricea.
            </div>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="mr-1.5 h-4 w-4" />
            Export CSV
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={!pendingCount}
                data-testid="reset-pending-btn"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Anulează ({pendingCount})
              </Button>
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={!pendingCount || saving}
                data-testid="save-changes-btn"
              >
                <Save className="mr-1.5 h-4 w-4" />
                Salvează ({pendingCount})
              </Button>
            </>
          )}
        </div>
      </div>

      {orphans.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="orphans-warning">
          <CardContent className="py-3 text-sm text-amber-900">
            ⚠️ {orphans.length} reguli orfane (câmpuri eliminate din catalog).
            {isAdmin && (
              <button
                className="ml-2 underline"
                onClick={async () => {
                  if (!window.confirm("Ștergi regulile orfane?")) return;
                  try {
                    const r = await axios.delete(`${API}/api/admin/field-access/rules/orphans`);
                    toast.success(`${r.data.deleted} reguli orfane șterse.`);
                    await fetchAll();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Eroare la ștergere");
                  }
                }}
                data-testid="cleanup-orphans-btn"
              >
                Curăță acum
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Iter38b — Profiles + CSV import toolbar (admin only) */}
      {isAdmin && (
        <Card data-testid="flac-admin-toolbar">
          <CardContent className="flex flex-wrap items-end gap-3 py-4">
            <div className="min-w-[260px]">
              <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
                <Sparkles className="inline h-3 w-3 mr-1" /> Profil predefinit
              </label>
              <Select
                value={selectedProfile}
                onValueChange={(v) => {
                  setSelectedProfile(v);
                  handlePreviewProfile(v);
                }}
              >
                <SelectTrigger data-testid="profile-select">
                  <SelectValue placeholder="Alege un profil..." />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 max-w-md pb-1.5">
              {selectedProfile
                ? profiles.find((p) => p.key === selectedProfile)?.description
                : "Aplică o configurație template pe întreaga matrice. Modificările sunt auditate."}
            </div>
            <div className="ml-auto flex items-center gap-2 pb-0.5">
              <label className="cursor-pointer" data-testid="csv-import-label">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleCsvFileChosen}
                  data-testid="csv-import-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  asChild
                  disabled={csvImporting}
                >
                  <span>
                    <Upload className="mr-1.5 h-4 w-4" />
                    {csvImporting ? "Se procesează..." : "Import CSV"}
                  </span>
                </Button>
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
              Caută
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Label, field_key, nume tehnic sau descriere..."
                className="pl-8"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
              Categorie
            </label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate ({catalog.fields.length})</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c} ({catalog.fields.filter((f) => f.category === c).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <input
              type="checkbox"
              id="sensitive-only"
              checked={showSensitiveOnly}
              onChange={(e) => setShowSensitiveOnly(e.target.checked)}
              data-testid="sensitive-filter"
            />
            <label htmlFor="sensitive-only" className="text-sm cursor-pointer">
              Doar date sensibile (PII)
            </label>
          </div>
          <div className="ml-auto text-sm text-slate-600" data-testid="results-count">
            {filteredFields.length} / {catalog.fields.length} câmpuri
          </div>
        </CardContent>
      </Card>

      {/* Matrix table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Matrice acces</CardTitle>
          <CardDescription>
            Pentru fiecare celulă: <Badge className={PERM_BADGE.hidden}>Ascuns</Badge>{" "}
            <Badge className={PERM_BADGE.read}>Citește</Badge>{" "}
            <Badge className={PERM_BADGE.write}>Editează</Badge>. Celulele cu fundal galben sunt
            modificări nesalvate.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="field-access-matrix">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 sticky left-0 bg-slate-50 z-10 min-w-[280px]">Câmp</th>
                  <th className="px-3 py-2">Categorie</th>
                  {catalog.roles.map((r) => (
                    <th key={r} className="px-3 py-2 text-center min-w-[140px]">
                      {ROLE_LABELS[r] || r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredFields.map((f) => (
                  <tr key={f.field_key} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`row-${f.field_key}`}>
                    <td className="px-4 py-2 sticky left-0 bg-white z-10">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {f.label}
                        {f.sensitive && (
                          <Badge variant="outline" className="text-[9px] uppercase border-rose-300 text-rose-700">
                            PII
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slate-500">{f.field_key}</div>
                      {f.description && (
                        <div className="mt-0.5 text-xs text-slate-600 max-w-md">{f.description}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]">{f.category}</Badge>
                    </td>
                    {catalog.roles.map((role) => {
                      const k = `${f.field_key}|${role}`;
                      const isPending = pending[k] !== undefined;
                      const perm = getEffectivePerm(f.field_key, role);
                      const source = rulesMatrix[f.field_key]?.[role]?.source || "default";
                      return (
                        <td
                          key={role}
                          className={`px-3 py-2 text-center ${isPending ? "bg-amber-50" : ""}`}
                        >
                          {isAdmin ? (
                            <Select
                              value={perm}
                              onValueChange={(v) => handleChange(f.field_key, role, v)}
                            >
                              <SelectTrigger
                                className={`h-8 ${PERM_SELECT_CLASS[perm]}`}
                                data-testid={`cell-${f.field_key}-${role}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {catalog.permissions.map((p) => (
                                  <SelectItem key={p} value={p}>{PERM_LABELS[p] || p}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={PERM_BADGE[perm]} data-testid={`cell-${f.field_key}-${role}`}>
                              {PERM_LABELS[perm] || perm}
                            </Badge>
                          )}
                          {source === "db" && !isPending && (
                            <div className="mt-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                              override
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {filteredFields.length === 0 && (
                  <tr>
                    <td colSpan={2 + catalog.roles.length} className="px-4 py-12 text-center text-sm text-slate-400">
                      Niciun câmp nu corespunde filtrelor.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="save-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmi salvarea?</DialogTitle>
            <DialogDescription>
              Vei modifica <strong>{pendingCount}</strong> celule din matricea de acces.
              Acțiunea este auditată.
            </DialogDescription>
          </DialogHeader>
          {pendingBlastRadius.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="save-blast-radius">
              <div className="flex items-center gap-1.5 font-medium text-amber-900 mb-1">
                <Users className="h-4 w-4" />
                Blast radius
              </div>
              <ul className="ml-5 list-disc text-amber-900">
                {pendingBlastRadius.map((b) => (
                  <li key={b.role}>
                    <strong>{b.count}</strong> utilizator{b.count === 1 ? "" : "i"} cu rolul <strong>{b.label}</strong> {b.count === 1 ? "va fi afectat" : "vor fi afectați"}.
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Anulează
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="confirm-save-btn">
              {saving ? "Se salvează..." : "Salvează modificările"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter38b — Profile apply confirmation */}
      <Dialog open={profileApplyOpen} onOpenChange={(open) => {
        setProfileApplyOpen(open);
        if (!open) { setSelectedProfile(""); setProfilePreview(null); }
      }}>
        <DialogContent className="max-w-2xl" data-testid="profile-apply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-700" />
              Aplică profil: <span className="font-mono text-violet-700">{selectedProfile}</span>
            </DialogTitle>
            <DialogDescription>
              {profilePreview?.change_count === 0 ? (
                <span>Profilul este deja activ — nicio modificare necesară.</span>
              ) : (
                <span>
                  Vor fi modificate <strong>{profilePreview?.change_count}</strong> celule din matrice.
                  Acțiunea este atomică și auditată.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {profilePreview && profilePreview.change_count > 0 && (
            <>
              {profilePreview.affected_roles?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="profile-blast-radius">
                  <div className="flex items-center gap-1.5 font-medium text-amber-900 mb-1">
                    <AlertTriangle className="h-4 w-4" /> Blast radius estimat
                  </div>
                  <ul className="ml-5 list-disc text-amber-900">
                    {profilePreview.affected_roles.map((r) => (
                      <li key={r}>
                        <strong>{profilePreview.affected_users_by_role?.[r] || 0}</strong> utilizator(i) cu rolul <strong>{ROLE_LABELS[r] || r}</strong>.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="max-h-[300px] overflow-y-auto rounded-lg border border-slate-200" data-testid="profile-diff-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-slate-500 uppercase tracking-wide">Câmp</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 uppercase tracking-wide">Rol</th>
                      <th className="px-2 py-1.5 text-center text-slate-500 uppercase tracking-wide">Vechi</th>
                      <th className="px-2 py-1.5 text-center text-slate-500 uppercase tracking-wide">Nou</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profilePreview.diff.slice(0, 100).map((d, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono text-slate-800">{d.field_key}</td>
                        <td className="px-2 py-1">{ROLE_LABELS[d.role] || d.role}</td>
                        <td className="px-2 py-1 text-center">
                          <Badge className={PERM_BADGE[d.old]}>{PERM_LABELS[d.old] || d.old}</Badge>
                        </td>
                        <td className="px-2 py-1 text-center">
                          <Badge className={PERM_BADGE[d.new]}>{PERM_LABELS[d.new] || d.new}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {profilePreview.diff.length > 100 && (
                  <div className="px-2 py-2 text-center text-xs text-slate-500 italic">
                    ... +{profilePreview.diff.length - 100} alte modificări
                  </div>
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileApplyOpen(false)} disabled={applying}>
              Anulează
            </Button>
            <Button
              onClick={handleApplyProfile}
              disabled={applying || !profilePreview || profilePreview.change_count === 0}
              data-testid="confirm-apply-profile-btn"
            >
              {applying ? "Se aplică..." : `Aplică profil (${profilePreview?.change_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter38b — CSV diff preview */}
      <Dialog open={csvDiffOpen} onOpenChange={(open) => {
        setCsvDiffOpen(open);
        if (!open) setCsvPreview(null);
      }}>
        <DialogContent className="max-w-2xl" data-testid="csv-diff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-sky-700" />
              Preview import CSV
            </DialogTitle>
            <DialogDescription>
              {csvPreview ? (
                <>
                  Fișier: <span className="font-mono">{csvPreview.filename}</span> · {csvPreview.row_count} celule parsate ·{" "}
                  <strong>{csvPreview.change_count}</strong> modificări vs. starea curentă.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {csvPreview && csvPreview.change_count > 0 && (
            <div className="max-h-[350px] overflow-y-auto rounded-lg border border-slate-200" data-testid="csv-diff-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-slate-500 uppercase tracking-wide">Câmp</th>
                    <th className="px-2 py-1.5 text-left text-slate-500 uppercase tracking-wide">Rol</th>
                    <th className="px-2 py-1.5 text-center text-slate-500 uppercase tracking-wide">Vechi</th>
                    <th className="px-2 py-1.5 text-center text-slate-500 uppercase tracking-wide">Nou</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.diff.slice(0, 100).map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono text-slate-800">{d.field_key}</td>
                      <td className="px-2 py-1">{ROLE_LABELS[d.role] || d.role}</td>
                      <td className="px-2 py-1 text-center">
                        <Badge className={PERM_BADGE[d.old]}>{PERM_LABELS[d.old] || d.old}</Badge>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Badge className={PERM_BADGE[d.new]}>{PERM_LABELS[d.new] || d.new}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvPreview.diff.length > 100 && (
                <div className="px-2 py-2 text-center text-xs text-slate-500 italic">
                  ... +{csvPreview.diff.length - 100} alte modificări
                </div>
              )}
            </div>
          )}
          {csvPreview && csvPreview.change_count === 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              ✓ Niciun diff — fișierul CSV reflectă deja matricea curentă.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsvDiffOpen(false)} disabled={csvImporting}>
              Anulează
            </Button>
            <Button
              onClick={handleApplyCsv}
              disabled={csvImporting || !csvPreview || csvPreview.change_count === 0}
              data-testid="confirm-apply-csv-btn"
            >
              {csvImporting ? "Se aplică..." : `Aplică modificările (${csvPreview?.change_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
